/**
 * NotificationsSection Component
 * Push and email notification preferences grid
 */

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { useNotificationSettings, useUpdateNotificationSettings } from "../hooks/useSettings"
import type { NotificationPreference } from "../types/settings.types"

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  ritual_reminders: "Ritual Reminders",
  streak_updates: "Streak Updates",
  community_activity: "Community Activity",
  teacher_updates: "Teacher Updates",
  message_invites: "Messages & Invites",
  app_announcements: "App Announcements",
}

const DEFAULT_PREFERENCES: NotificationPreference[] = [
  {
    id: "ritual_reminders",
    label: "Ritual Reminders",
    description: "Get reminders for upcoming rituals and your daily or weekly practice.",
    push: true,
    email: true,
  },
  {
    id: "streak_updates",
    label: "Streak Updates",
    description: "Stay motivated with quick updates about your progress and streaks.",
    push: true,
    email: false,
  },
  {
    id: "community_activity",
    label: "Community Activity",
    description: "See when your communities post new rituals, events, or discussions.",
    push: true,
    email: true,
  },
  {
    id: "teacher_updates",
    label: "Teacher Updates",
    description: "Get notified when teachers you follow publish new content.",
    push: false,
    email: true,
  },
  {
    id: "message_invites",
    label: "Messages & Invites",
    description: "Alerts for new messages, ritual invites, and connection requests.",
    push: true,
    email: true,
  },
  {
    id: "app_announcements",
    label: "App Announcements",
    description: "Important updates about new features, improvements, or system changes.",
    push: false,
    email: true,
  },
]

export function NotificationsSection() {
  const { data, isLoading } = useNotificationSettings()
  const { mutate: updateNotifications, isPending } = useUpdateNotificationSettings()
  const [preferences, setPreferences] = useState<NotificationPreference[]>(DEFAULT_PREFERENCES)
  const [originalPreferences, setOriginalPreferences] = useState<NotificationPreference[] | null>(
    null,
  )

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    originalPreferences !== null &&
    JSON.stringify(preferences) !== JSON.stringify(originalPreferences)

  // Sync server data into local state
  useEffect(() => {
    if (data?.settings && data.settings.length > 0) {
      const serverPrefs = data.settings.map(s => {
        const defaultPref = DEFAULT_PREFERENCES.find(d => d.id === s.type)
        return {
          id: s.type,
          label: NOTIFICATION_TYPE_LABELS[s.type] || s.type,
          description: defaultPref?.description || "",
          push: s.sendPushNotification,
          email: s.sendEmailNotification,
        }
      })
      setPreferences(serverPrefs)
      setOriginalPreferences(serverPrefs)
    }
  }, [data])

  const handleToggle = (id: string, channel: "push" | "email") => {
    setPreferences(prev =>
      prev.map(pref => (pref.id === id ? { ...pref, [channel]: !pref[channel] } : pref)),
    )
  }

  const handleSave = () => {
    const settings = preferences.map(pref => ({
      type: pref.id,
      sendPushNotification: pref.push,
      sendEmailNotification: pref.email,
    }))
    updateNotifications(settings)
  }

  const handleReset = () => {
    if (originalPreferences) {
      setPreferences(originalPreferences)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="text-2xl font-bold text-charcoal uppercase section-heading">
            Notifications
          </h1>
          <p className="text-sm text-stone mt-1">Loading preferences...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Section Header */}
      <div>
        <h1 className="text-2xl font-bold text-charcoal uppercase section-heading">
          Notifications
        </h1>
      </div>

      {/* Notification Grid */}
      <div className="flex flex-col gap-0">
        {/* Header Row */}
        <div className="grid grid-cols-[1fr_80px_80px] gap-4 pb-3 border-0">
          <span className="text-xs font-semibold uppercase text-stone">
            Choose what you want to stay updated about.
          </span>
          <span className="text-xs font-semibold uppercase text-stone text-center">Push</span>
          <span className="text-xs font-semibold uppercase text-stone text-center">Email</span>
        </div>

        {/* Preference Rows */}
        {preferences.map(pref => (
          <div key={pref.id} className="grid grid-cols-[1fr_80px_80px] gap-2 py-4 border-0">
            <div>
              <p className="text-sm font-semibold text-charcoal">{pref.label}</p>
              <p className="text-[13px] text-charcoal">{pref.description}</p>
            </div>
            <div className="flex justify-center">
              <Checkbox
                checked={pref.push}
                onCheckedChange={() => handleToggle(pref.id, "push")}
                aria-label={`${pref.label} push notification`}
              />
            </div>
            <div className="flex justify-center">
              <Checkbox
                checked={pref.email}
                onCheckedChange={() => handleToggle(pref.id, "email")}
                aria-label={`${pref.label} email notification`}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4 mt-2">
        <Button
          variant="primary"
          size="md"
          className="px-20 uppercase"
          onClick={handleSave}
          isLoading={isPending}
        >
          Save
        </Button>
        <button
          type="button"
          onClick={handleReset}
          disabled={!hasUnsavedChanges}
          className={`text-sm font-medium transition-colors ${
            hasUnsavedChanges
              ? "text-stone hover:text-charcoal cursor-pointer"
              : "text-stone/50 cursor-not-allowed"
          }`}
        >
          Reset
        </button>
      </div>
    </div>
  )
}
